/*
 * Copyright (c) 2024 Amazon.com, Inc. All Rights Reserved.
 */
import React, { Dispatch, SetStateAction, useRef, useState } from "react";
import "./NumberFilter.css";
import { NumberFilterValues } from "../../model/FilterValues";
import { useOutsideClick } from "../../hooks/useOutsideClick";

/**
 * @author stcatai Mar 25, 2024
 */
export interface NumberFilterProps {
  values: NumberFilterValues;
  setValues: Dispatch<SetStateAction<NumberFilterValues>>;
  setFetch: Dispatch<SetStateAction<NumberFilterValues>>;
}

export default function NumberFilter({values, setValues, setFetch}: NumberFilterProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [greater, setGreater] = useState<number | undefined>(values.greater);
  const [less, setLess] = useState<number | undefined>(values.less);
  const [equal, setEqual] = useState<number | undefined>(values.equal);

  const contentRef = useRef(null);
  const openButtonRef = useRef(null);

  useOutsideClick([contentRef, openButtonRef], () => {
    setIsOpen(false);
  });

  const submitValues = () => {
    setValues({greater: greater, less: less, equal: equal});
    setFetch({greater: greater, less: less, equal: equal});
    setIsOpen(false);
  }

  const clearValues = () => {
    setGreater(undefined);
    setLess(undefined);
    setEqual(undefined);
    setValues({greater: undefined, less: undefined, equal: undefined});
    setFetch({greater: undefined, less: undefined, equal: undefined});
    setIsOpen(false);
  }

  return <div className="number-filter">
    <button className="open-button" onClick={() => setIsOpen(!isOpen)} ref={openButtonRef}>&#9660;</button>
    {isOpen && <div className="content" ref={contentRef}>
      <label htmlFor="greater">&gt;</label>
      <input type="number" id="greater" placeholder="Greater than" value={greater || ''} readOnly={equal !== undefined && !isNaN(equal)} onChange={(e) => setGreater(e.target.valueAsNumber)}/>
      <br/>

      <label htmlFor="less">&lt;</label>
      <input type="number" id="less" placeholder="Less than" value={less || ''} readOnly={equal !== undefined && !isNaN(equal)} onChange={(e) => setLess(e.target.valueAsNumber)}/>
      <br/>

      <label htmlFor="equal">=</label>
      <input type="number" id="equal" placeholder="Equal to" value={equal || ''} onChange={(e) => setEqual(e.target.valueAsNumber)}/>

      <div className="update-buttons">
        <button onClick={submitValues}>Set</button>
        <button onClick={clearValues}>Clear</button>
      </div>
    </div> }
  </div>
}